import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useAdjustDraftStore} from '@/store/audit';
import {computeCommonTotals} from '@/utils/dataUtils';
import {resolveMultiWpPerCheckNbvEndpoints, priorYearOfAssessment} from '@/utils/wpPriorPeriodBfRollForward';
import {
    getWpYoaMapCache,
    projectYoaBucketToWorkingPaperData,
} from '@/services/auditService/wpYoaMapCache';
import {useWpPerCheckFollowActiveTab} from '@/adjustment/hooks/WpPeriodScopeContext';

/**
 * MULTI seamless Period Tabs chrome helpers (PPE / Tax / PL Total WP).
 * - Header period = engagement span (first.from ~ last.to) so Tab switch does not rewrite header.
 * - EpWorkingContent meta is pinned across Tab switches (#20: SINGLE unchanged).
 * - WpPerCheck: default = last-tab Final / first-tab LastYear (PPE);
 *   PL Total (`wpPerCheckFollowActiveTab`) = follow active Tab Total final.
 */

export function resolveEngagementHeaderPeriod(payload: {
    periods?: Array<{periodFrom?: string; periodTo?: string} | null> | null;
    periodFrom?: string | null;
    periodTo?: string | null;
} | null | undefined, metaPeriodFrom?: string | null, metaPeriodTo?: string | null): {
    periodFrom: string | null | undefined;
    periodTo: string | null | undefined;
} {
    const periods = Array.isArray(payload?.periods) ? payload!.periods!.filter(Boolean) : [];
    if (periods.length >= 2) {
        const first = periods[0];
        const last = periods[periods.length - 1];
        return {
            periodFrom: first?.periodFrom || metaPeriodFrom || payload?.periodFrom || null,
            periodTo: last?.periodTo || metaPeriodTo || payload?.periodTo || null,
        };
    }
    return {
        periodFrom: metaPeriodFrom ?? payload?.periodFrom ?? null,
        periodTo: metaPeriodTo ?? payload?.periodTo ?? null,
    };
}

function hasEpWorkingMeta(smeFrs: unknown, wokeDone: any): boolean {
    const okSme = Array.isArray(smeFrs) && smeFrs.length > 0;
    const okWoke = !!wokeDone && (
        (Array.isArray(wokeDone.wd) && wokeDone.wd.length > 0)
        || (wokeDone.dynamicWdMaps && Object.keys(wokeDone.dynamicWdMaps).length > 0)
    );
    return okSme || okWoke;
}

export type SeamlessEpWorkingMeta = {
    smeFrs: any;
    wokeDone: any;
};

/**
 * Pin SME-FRS / WOKE_DONE across seamless Period Tab switches so EpWorkingContent
 * stays outside the tab body and does not flash empty / swap dictionaries.
 */
export function usePinnedEpWorkingMeta(opts: {
    open: boolean;
    isMultiPeriodTabs: boolean;
    apiCode?: string | null;
    currentData: any;
    periods?: Array<{key?: string} | null> | null;
    /** When sliceKey changes, re-evaluate pin from drafts */
    sliceKey?: string | null;
}): SeamlessEpWorkingMeta {
    const {
        open,
        isMultiPeriodTabs,
        apiCode,
        currentData,
        periods,
        sliceKey,
    } = opts;

    const pinnedRef = useRef<SeamlessEpWorkingMeta | null>(null);

    useEffect(() => {
        if (!isMultiPeriodTabs) return;
        if (!open) {
            pinnedRef.current = null;
        }
    }, [open, isMultiPeriodTabs]);

    return useMemo(() => {
        const fromCurrent = {
            smeFrs: currentData?.['SME-FRS'],
            wokeDone: currentData?.['WOKE_DONE'],
        };

        if (!isMultiPeriodTabs) {
            return fromCurrent;
        }

        if (hasEpWorkingMeta(fromCurrent.smeFrs, fromCurrent.wokeDone)) {
            pinnedRef.current = fromCurrent;
            return fromCurrent;
        }

        const code = String(apiCode || '').trim().toUpperCase();
        const list = Array.isArray(periods) ? periods : [];
        if (code) {
            const draftStore = useAdjustDraftStore.getState();
            for (const p of list) {
                if (!p?.key) continue;
                const detail = draftStore.getUnifiedDetail(`${code}__${p.key}`)
                    || draftStore.getUnifiedDetail(code);
                if (!detail || typeof detail !== 'object') continue;
                const smeFrs = (detail as any)['SME-FRS'];
                const wokeDone = (detail as any)['WOKE_DONE'];
                if (hasEpWorkingMeta(smeFrs, wokeDone)) {
                    const resolved = {smeFrs, wokeDone};
                    pinnedRef.current = resolved;
                    return resolved;
                }
            }
        }

        if (pinnedRef.current && hasEpWorkingMeta(pinnedRef.current.smeFrs, pinnedRef.current.wokeDone)) {
            return pinnedRef.current;
        }
        return fromCurrent;
    }, [
        isMultiPeriodTabs,
        apiCode,
        currentData,
        periods,
        sliceKey,
    ]);
}

export function useSeamlessWpChrome(opts: {
    open: boolean;
    /** WP payload is per-component; only seamless / periods / period fields are read */
    payload?: Record<string, any> | null;
    apiCode?: string | null;
    currentData?: any;
    metaPeriodFrom?: string | null;
    metaPeriodTo?: string | null;
}) {
    const isMultiPeriodTabs = !!opts.payload?.seamlessPeriodTabs;
    const headerPeriod = useMemo(
        () => resolveEngagementHeaderPeriod(
            opts.payload,
            opts.metaPeriodFrom,
            opts.metaPeriodTo,
        ),
        [
            opts.payload?.periods,
            opts.payload?.periodFrom,
            opts.payload?.periodTo,
            opts.metaPeriodFrom,
            opts.metaPeriodTo,
            isMultiPeriodTabs,
        ],
    );

    const epWorkingMeta = usePinnedEpWorkingMeta({
        open: opts.open,
        isMultiPeriodTabs,
        apiCode: opts.apiCode,
        currentData: opts.currentData,
        periods: opts.payload?.periods,
        sliceKey: opts.payload?.periodScope?.sliceKey,
    });

    return {
        isMultiPeriodTabs,
        /** Prefer engagement span when MULTI tabs; else meta/payload period */
        headerPeriodFrom: isMultiPeriodTabs
            ? headerPeriod.periodFrom
            : (opts.metaPeriodFrom ?? opts.payload?.periodFrom ?? null),
        headerPeriodTo: isMultiPeriodTabs
            ? headerPeriod.periodTo
            : (opts.metaPeriodTo ?? opts.payload?.periodTo ?? null),
        epWorkingMeta,
    };
}

/** Normalize draft / YOA-bucket / WorkingPaperData into a mergeable detail bag. */
export function unwrapWpDetailBag(raw: any): any | null {
    if (!raw || typeof raw !== 'object') return null;
    if (Array.isArray(raw.details) && raw.details[0] && typeof raw.details[0] === 'object' && !Array.isArray(raw.details[0])) {
        return {...raw.details[0]};
    }
    if (raw.details && typeof raw.details === 'object' && !Array.isArray(raw.details)) {
        return {...raw.details};
    }
    return raw;
}

/** Pull a named row array from a WP detail bag (commonRow default). */
export function extractWpDetailRows(detail: any, fieldName = 'commonRow'): any[] {
    const bag = unwrapWpDetailBag(detail);
    if (!bag || typeof bag !== 'object') return [];
    const rows = (bag as any)[fieldName];
    return Array.isArray(rows) ? rows : [];
}

/** Default Final Amount endpoints for commonRow-based WPs. */
export function sumCommonRowPerCheckFinal(detail: any): number {
    return computeCommonTotals(extractWpDetailRows(detail, 'commonRow')).perCheckFinal;
}

export function sumCommonRowLastYearFinal(detail: any): number {
    return computeCommonTotals(extractWpDetailRows(detail, 'commonRow')).lastYearFinal;
}

export type MultiWpPerCheckEndpoints = {
    currentValue: number;
    lastYearValue: number;
    yoaLabel: string | null;
    lastYoaLabel: string | null;
} | null;

type PeriodLike = {
    key?: string;
    yearOfAssessment?: string | null;
    periodFrom?: string | null;
    periodTo?: string | null;
};

/**
 * MULTI WpPerCheck (outside Period Tabs).
 *
 * Default (PPE / BS whitelist): last-tab Final (c/f) / first-tab LastYear (b/f);
 * YOA labels stable across Tab switches.
 *
 * PL TotalSingle (`wpPerCheckFollowActiveTab`): Total final + YOA follow the active Period Tab.
 * SINGLE / enabled=false → null (caller keeps tab-local totals).
 */
export function useMultiWpPerCheckEndpoints(opts: {
    enabled: boolean;
    apiCode?: string | null;
    companyId?: number | null;
    annualId?: number | null;
    periods?: PeriodLike[] | null;
    activeSliceKey?: string | null;
    activeCurrent: number;
    activeLastYear: number;
    sumDetailCurrent: (detail: any, period: PeriodLike) => number;
    sumDetailLastYear: (detail: any, period: PeriodLike) => number;
    fallbackYoa?: string | null;
    fallbackLastYoa?: string | null;
    /**
     * When true, PerCheck uses active-tab Final (and YOA labels).
     * Defaults from WpPerCheckFollowActiveTabContext (PL Total → registry WP).
     */
    followActiveTab?: boolean;
}): MultiWpPerCheckEndpoints {
    const {
        enabled,
        apiCode,
        companyId,
        annualId,
        periods,
        activeSliceKey,
        activeCurrent,
        activeLastYear,
        sumDetailCurrent,
        sumDetailLastYear,
        fallbackYoa,
        fallbackLastYoa,
        followActiveTab: followActiveTabOpt,
    } = opts;

    const followActiveTabFromCtx = useWpPerCheckFollowActiveTab();
    const followActiveTab = followActiveTabOpt ?? followActiveTabFromCtx;

    const detailRevision = useAdjustDraftStore((s) => (enabled ? s.detailRevision : 0));
    /** Pin engagement lastYoa from first Tab so lastYoaLabel never follows tab-shifted meta.lastYoa. */
    const pinnedEngagementLastYoaRef = useRef<string | null>(null);

    useEffect(() => {
        if (!enabled) {
            pinnedEngagementLastYoaRef.current = null;
        }
    }, [enabled]);

    const getDetailForPeriod = useCallback((period: PeriodLike) => {
        const code = String(apiCode || '').trim().toUpperCase();
        if (!code) return null;
        const sliceKey = String(period.key || '').trim();
        if (sliceKey) {
            const draft = useAdjustDraftStore.getState().getUnifiedDetail(`${code}__${sliceKey}`);
            if (draft && typeof draft === 'object' && Object.keys(draft).length > 0) {
                return unwrapWpDetailBag(draft) ?? draft;
            }
        }
        const yoaKey = String(period.yearOfAssessment || '').trim();
        const cid = Number(companyId) || 0;
        const aid = Number(annualId) || 0;
        if (cid > 0 && aid > 0 && yoaKey) {
            const cache = getWpYoaMapCache(cid, aid, code);
            if (cache?.byYearOfAssessment?.[yoaKey]) {
                return projectYoaBucketToWorkingPaperData(cache, yoaKey);
            }
        }
        return null;
    }, [apiCode, companyId, annualId]);

    return useMemo(() => {
        if (!enabled || !apiCode) return null;
        const list = Array.isArray(periods) ? periods : [];
        const first = list[0];
        const firstKey = String(first?.key || '').trim();
        const activeKey = String(activeSliceKey || '').trim();
        const onFirstTab = !!firstKey && !!activeKey && activeKey === firstKey;
        const fallback = fallbackLastYoa ? String(fallbackLastYoa).trim() : '';

        // PL Total → multi Tabs: Total final / YOA track the active Period Tab
        if (followActiveTab) {
            if (list.length < 1) {
                return {
                    currentValue: Number(activeCurrent) || 0,
                    lastYearValue: Number(activeLastYear) || 0,
                    yoaLabel: fallbackYoa ? String(fallbackYoa).trim() || null : null,
                    lastYoaLabel: fallback || null,
                };
            }
            const active = (activeKey
                ? list.find((p) => String(p?.key || '').trim() === activeKey)
                : null) || first;
            return {
                currentValue: Number(activeCurrent) || 0,
                lastYearValue: Number(activeLastYear) || 0,
                yoaLabel: active?.yearOfAssessment || fallbackYoa || null,
                lastYoaLabel: fallback
                    || priorYearOfAssessment(active?.yearOfAssessment)
                    || null,
            };
        }

        if (onFirstTab && fallback) {
            pinnedEngagementLastYoaRef.current = fallback;
        }
        const stableFallbackLastYoa = pinnedEngagementLastYoaRef.current
            || (onFirstTab ? fallback : '')
            || priorYearOfAssessment(first?.yearOfAssessment)
            || null;

        return resolveMultiWpPerCheckNbvEndpoints({
            periods: list,
            activeSliceKey,
            activeNbvCf: activeCurrent,
            activeNbvBf: activeLastYear,
            getDetailForPeriod,
            sumDetailNbvCf: (detail, period) => sumDetailCurrent(detail, period),
            sumDetailNbvBf: (detail, period) => sumDetailLastYear(detail, period),
            fallbackYoa,
            fallbackLastYoa: stableFallbackLastYoa,
        });
    }, [
        enabled,
        apiCode,
        periods,
        activeSliceKey,
        activeCurrent,
        activeLastYear,
        getDetailForPeriod,
        sumDetailCurrent,
        sumDetailLastYear,
        fallbackYoa,
        fallbackLastYoa,
        detailRevision,
        followActiveTab,
    ]);
}
